/**
 * Severity Options Utility
 *
 * Centralized severity level configuration used across 15+ integrations
 * for notification threshold settings.
 *
 * Severity levels (cumulative):
 * - C: Critical only
 * - HC: High + Critical
 * - MHC: Medium + High + Critical
 * - LMHC: Low + Medium + High + Critical
 * - ILMHC: Info + Low + Medium + High + Critical (all levels)
 */

import type {
  SeverityLevel,
  SeverityOption,
} from '@/features/integrations/types/configuration.types';

/**
 * Generate severity options for select dropdowns
 * Used by notification integrations (Slack, Email, Teams, Webhook, etc.)
 *
 * @returns Array of severity options with labels and cumulative values
 */
export function generateSeverityOptions(): SeverityOption[] {
  return [
    { label: 'Critical', value: 'C' },
    { label: 'High', value: 'HC' },
    { label: 'Medium', value: 'MHC' },
    { label: 'Low', value: 'LMHC' },
    { label: 'Info', value: 'ILMHC' },
  ];
}

/**
 * Get human-readable label for a severity level
 *
 * @param level - Severity level code
 * @returns Human-readable label
 *
 * @example
 * getSeverityLabel('MHC') // Returns: 'Medium'
 * getSeverityLabel('C') // Returns: 'Critical'
 */
export function getSeverityLabel(level: SeverityLevel): string {
  const options = generateSeverityOptions();
  const option = options.find(opt => opt.value === level);
  return option?.label || level;
}

/**
 * Get all severity levels included in a threshold
 *
 * @param threshold - Severity threshold level
 * @returns Array of individual severity levels included
 *
 * @example
 * getSeverityLevelsInThreshold('MHC')
 * // Returns: ['M', 'H', 'C'] (Medium, High, Critical)
 */
export function getSeverityLevelsInThreshold(
  threshold: SeverityLevel
): string[] {
  const mapping: Record<SeverityLevel, string[]> = {
    C: ['C'],
    HC: ['H', 'C'],
    MHC: ['M', 'H', 'C'],
    LMHC: ['L', 'M', 'H', 'C'],
    ILMHC: ['I', 'L', 'M', 'H', 'C'],
  };
  return mapping[threshold] || [];
}

/**
 * Check if a severity level meets the threshold
 *
 * @param level - Single severity level (I, L, M, H, C)
 * @param threshold - Severity threshold
 * @returns true if level meets or exceeds threshold
 *
 * @example
 * meetsSeverityThreshold('H', 'MHC') // true (High is in Medium+High+Critical)
 * meetsSeverityThreshold('L', 'HC') // false (Low is below High+Critical)
 */
export function meetsSeverityThreshold(
  level: string,
  threshold: SeverityLevel
): boolean {
  const includedLevels = getSeverityLevelsInThreshold(threshold);
  return includedLevels.includes(level);
}

/**
 * Default severity threshold for new integrations
 */
export const DEFAULT_SEVERITY_THRESHOLD: SeverityLevel = 'MHC';

/**
 * Severity info text used in field descriptions
 */
export const SEVERITY_INFO_TEXT =
  'Select the minimum risk severity level at which Chariot should send notifications.';
